// Tennis Dashboard — the Tennis workspace's own overview, in the flagship
// editorial language (clay-court hero, Playfair statement, lifting stat tiles,
// premium product board). Tennis-native data only; honest-empty until the
// engine publishes real picks — never football data, never fabricated.
import { Link } from "react-router-dom";
import {
  Card,
  Badge,
  Kicker,
  AwaitingPanel,
  StatusPill,
  StatTile,
  Grid,
  Stack,
  daysUntil,
  R1_DATE,
} from "../_kit.tsx";
import { Icon } from "../../../components/icons/Icon.tsx";
import { useUserTier } from "../../../auth/useUserTier.ts";
import { tierMeets } from "../../../auth/tier-gates.ts";
import { useTennisDashboard, type TennisProductStatus } from "./useTennisDashboard.ts";

function fmtEdge(e: number | null): string {
  return e !== null ? `+${e.toFixed(1)}%` : "—";
}
function fmtOdds(o: number | null): string {
  return o !== null ? o.toFixed(2) : "—";
}

function ProductCard({ p, tierCode }: { p: TennisProductStatus; tierCode: string | null }): JSX.Element {
  const cls = `tn-pcard tn-pcard--${p.family}${p.published ? "" : " tn-pcard--awaiting"}`;
  // Tier chip is an upsell — only show it when the viewer doesn't already have access.
  const showTier = !tierMeets(tierCode, p.tier);
  return (
    <Link to={`/exchange/tennis/p/${p.slug}`} className={cls}>
      <div className="tn-pcard__top">
        <div className="tn-pcard__name">{p.name}</div>
        <Icon name="chev" size={13} className="tn-pcard__go" />
      </div>
      <div className="tn-pcard__tags">
        <Badge tone={p.family === "volume" ? "tennis" : "info"}>{p.family === "volume" ? "Volume" : "Layer"}</Badge>
        {showTier ? <Badge tone="muted">{p.tierLabel}</Badge> : null}
      </div>
      {p.published ? (
        <div className="tn-pcard__stats">
          <div className="tn-pcard__stat">
            <span className="tn-pcard__sv">{p.pickCount}</span>
            <span className="tn-pcard__sl">picks</span>
          </div>
          <div className="tn-pcard__stat">
            <span className="tn-pcard__sv tn-pcard__sv--green">{fmtEdge(p.topEdge)}</span>
            <span className="tn-pcard__sl">top edge</span>
          </div>
          <div className="tn-pcard__stat">
            <span className="tn-pcard__sv">{fmtOdds(p.combinedOdds)}</span>
            <span className="tn-pcard__sl">{p.combinedOdds !== null ? "comb. odds" : "—"}</span>
          </div>
          <span className="tn-pcard__pill"><StatusPill live>Live</StatusPill></span>
        </div>
      ) : (
        <div className="tn-pcard__await">
          <span className="tn-ghost__bar tn-ghost__bar--w1" />
          <StatusPill>Awaiting</StatusPill>
        </div>
      )}
    </Link>
  );
}

export function TennisDashboard(): JSX.Element {
  const { loading, products, totals, tournament, latestDate } = useTennisDashboard();
  const { tierCode } = useUserTier();
  const live = daysUntil(R1_DATE) === 0;
  const round1 = daysUntil(R1_DATE);
  const r1Label = round1 === 0 ? "Live now" : round1 === 1 ? "Tomorrow" : `in ${round1} days`;
  const volume = products.filter((p) => p.family === "volume");
  const layers = products.filter((p) => p.family === "layer");

  return (
    <div className="tn">
      <Stack>
      {/* Flagship hero — clay-court motif, statement + tournament aside. */}
      <section className="tn-hero">
        <div className="tn-hero__main">
          <Kicker green>Tennis · Dashboard</Kicker>
          <h1 className="tn-hero__statement">
            Every tennis product, <em>one live desk</em>.
          </h1>
          <p className="tn-hero__sub">
            Volume plays and intelligence layers on a single board — each lights up the
            moment the pipeline prices a genuine qualifying edge. Honest by design, never padded.
          </p>
          <div className="tn-hero__chips">
            <Badge tone="tennis">{tournament ?? "French Open 2026"}</Badge>
            <Badge tone="info">Clay</Badge>
            <StatusPill live={live}>{live ? "Pilot · live" : "Pilot · pre-tournament"}</StatusPill>
          </div>
        </div>
        <aside className="tn-hero__aside">
          <dl className="tn-def">
            <div className="tn-def__row">
              <dt className="tn-def__k">Round 1</dt>
              <dd className="tn-def__v tn-def__v--green">{r1Label}</dd>
            </div>
            <div className="tn-def__row">
              <dt className="tn-def__k">Products live</dt>
              <dd className="tn-def__v">{totals.live}/{totals.total}</dd>
            </div>
            <div className="tn-def__row">
              <dt className="tn-def__k">Picks today</dt>
              <dd className="tn-def__v">{totals.picks > 0 ? totals.picks : "—"}</dd>
            </div>
            <div className="tn-def__row">
              <dt className="tn-def__k">Top edge</dt>
              <dd className={`tn-def__v${totals.topEdge !== null ? " tn-def__v--green" : ""}`}>{fmtEdge(totals.topEdge)}</dd>
            </div>
          </dl>
        </aside>
      </section>

      <Grid>
        <StatTile
          label="Live products"
          value={`${totals.live}/${totals.total}`}
          sub={totals.live > 0 ? "publishing picks" : "awaiting first publish"}
          tone={totals.live > 0 ? "green" : "muted"}
        />
        <StatTile label="Picks published" value={totals.picks > 0 ? totals.picks : "—"} sub="across all products" />
        <StatTile label="Top edge today" value={fmtEdge(totals.topEdge)} sub="best qualifying BET" tone={totals.topEdge !== null ? "green" : "muted"} />
        <StatTile
          label="Round 1"
          value={round1 === 0 ? "Live" : round1 === 1 ? "Tom." : `${round1}d`}
          sub={latestDate ? `last publish ${latestDate}` : "Roland Garros 2026"}
        />
      </Grid>

      {!loading && totals.live === 0 ? (
        <Card accent eyebrow="Awaiting data" title="The board fills as the pipeline runs">
          <AwaitingPanel
            eyebrow="No fabricated picks"
            title="Every product below stays honestly empty until real signals exist"
            body="Picks appear only when Round 1 prices the draw and a permitting day produces genuine qualifying BETs. Nothing here is padded to look busy."
            countdown={{ value: round1 === 0 ? "Live" : round1, label: round1 === 0 ? "Round 1 underway" : "days to Round 1" }}
            ghosts={0}
          />
        </Card>
      ) : null}

      <Card accent eyebrow={`Volume products${tournament ? ` · ${tournament}` : ""}`} title="Daily volume board">
        <div className="tn-pboard">
          {volume.map((p) => (
            <ProductCard key={p.slug} p={p} tierCode={tierCode} />
          ))}
        </div>
      </Card>

      <Card eyebrow="Intelligence layers" title="Layer board">
        <div className="tn-pboard">
          {layers.map((p) => (
            <ProductCard key={p.slug} p={p} tierCode={tierCode} />
          ))}
        </div>
      </Card>

      <Card>
        <div className="tn-dash-foot">
          <div className="tn-dash-foot__links">
            <Link to="/exchange/tennis/signals" className="tn-cta tn-cta--ghost">Signals →</Link>
            <Link to="/exchange/tennis/tournament" className="tn-cta tn-cta--ghost">Tournament →</Link>
            <Link to="/exchange/tennis/no-bets" className="tn-cta tn-cta--ghost">No-Bet Report →</Link>
          </div>
          <Kicker>Tennis-only desk — never football data</Kicker>
        </div>
      </Card>
      </Stack>
    </div>
  );
}
